import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { RecipeBox, RecipeBoxInvitation, RecipeBoxMember, RecipeBoxRole } from "@/lib/types";
import { PlusCircle } from "lucide-react";
import type { FormEvent } from "react";
import { InvitationListItem } from "./InvitationListItem";
import { MemberListItem } from "./MemberListItem";
import { MembersPreview, type MemberPreviewItem } from "./MembersPreview";

export type MembersDialogProps = Readonly<{
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  recipeBox: RecipeBox | null;
  members: RecipeBoxMember[];
  pendingInvitations: RecipeBoxInvitation[];
  invitationsError: string | null;
  invitationsLoading: boolean;
  isOwner: boolean;
  inviteEmail: string;
  inviteName: string;
  inviteRole: RecipeBoxRole;
  onInviteEmailChange: (value: string) => void;
  onInviteNameChange: (value: string) => void;
  onInviteRoleChange: (role: RecipeBoxRole) => void;
  onInviteSubmit: (event: FormEvent<HTMLFormElement>) => void;
  isSendingInvite: boolean;
  memberPreviewItems: MemberPreviewItem[];
  remainingPreviewCount: number;
  totalMemberCount: number;
  currentUserId?: string | null;
  onRemoveMember: (member: RecipeBoxMember) => void;
  onRoleChange: (member: RecipeBoxMember, newRole: RecipeBoxRole) => void;
  removingMemberId: string | null;
  changingRoleMemberId: string | null;
  onCancelInvitation: (invitation: RecipeBoxInvitation) => void;
  onResendInvitation: (invitation: RecipeBoxInvitation) => void;
  activeInvitationAction: { id: string; type: "cancel" | "resend" } | null;
}>;

export const MembersDialog = ({
  isOpen,
  onOpenChange,
  recipeBox,
  members,
  pendingInvitations,
  invitationsError,
  invitationsLoading,
  isOwner,
  inviteEmail,
  inviteName,
  inviteRole,
  onInviteEmailChange,
  onInviteNameChange,
  onInviteRoleChange,
  onInviteSubmit,
  isSendingInvite,
  memberPreviewItems,
  remainingPreviewCount,
  totalMemberCount,
  currentUserId,
  onRemoveMember,
  onRoleChange,
  removingMemberId,
  changingRoleMemberId,
  onCancelInvitation,
  onResendInvitation,
  activeInvitationAction,
}: MembersDialogProps) => {
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button size="sm" className="gap-1">
          <PlusCircle className="h-3.5 w-3.5" />
          Members
          {totalMemberCount > 0 && (
            <MembersPreview
              items={memberPreviewItems}
              remaining={remainingPreviewCount}
              total={totalMemberCount}
              isLoading={members.length === 0 && pendingInvitations.length === 0 && invitationsLoading}
            />
          )}
          <Badge variant="secondary" className="font-medium">
            {totalMemberCount}
          </Badge>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-xl flex flex-col max-h-[90vh]">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="font-headline">Manage Members</DialogTitle>
          <DialogDescription>Review who has access and send or manage invitations.</DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-6 pr-2">
          {invitationsError ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
              We couldn&apos;t load invitations. Try again later.
            </div>
          ) : null}

          {isOwner ? (
          <form onSubmit={onInviteSubmit} className="space-y-3">
            <div className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="members-invite-name">Name (optional)</Label>
                <Input
                  id="members-invite-name"
                  type="text"
                  autoComplete="name"
                  placeholder="Recipient's name"
                  value={inviteName}
                  onChange={(event) => onInviteNameChange(event.target.value)}
                  disabled={isSendingInvite}
                  className="w-full"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="members-invite-email">Email address</Label>
                <Input
                  id="members-invite-email"
                  type="email"
                  autoComplete="email"
                  placeholder="name@example.com"
                  value={inviteEmail}
                  onChange={(event) => onInviteEmailChange(event.target.value)}
                  disabled={isSendingInvite}
                  required
                  className="w-full"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="members-invite-role">Role</Label>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Select 
                    value={inviteRole} 
                    onValueChange={(value) => onInviteRoleChange(value as RecipeBoxRole)} 
                    disabled={isSendingInvite}
                  >
                    <SelectTrigger id="members-invite-role" className="flex-1 sm:w-[140px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="viewer">Viewer</SelectItem>
                      <SelectItem value="member">Member</SelectItem>
                      <SelectItem value="owner">Owner</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    type="submit"
                    className="whitespace-nowrap flex-1 sm:flex-initial"
                    disabled={isSendingInvite || inviteEmail.trim() === ""}
                  >
                    {isSendingInvite ? "Sending..." : "Send Invitation"}
                  </Button>
                </div>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Invitations expire after 7 days. Email is required.
            </p>
          </form>
        ) : null}

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">Members</h3>
            <Badge variant="outline" className="font-medium uppercase">
              {members.length}
            </Badge>
          </div>
          <div className="space-y-3">
            {members.map((member) => (
              <MemberListItem
                key={member.uid}
                member={member}
                recipeBox={recipeBox}
                currentUserId={currentUserId}
                onRemove={onRemoveMember}
                onRoleChange={onRoleChange}
                isProcessing={removingMemberId === member.uid}
                isChangingRole={changingRoleMemberId === member.uid}
              />
            ))}
            {members.length === 0 ? <p className="text-sm text-muted-foreground">No accepted members yet.</p> : null}
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">Invitations</h3>
            <Badge variant="outline" className="font-medium uppercase">
              {pendingInvitations.length}
            </Badge>
          </div>
          <div className="space-y-3">
            {pendingInvitations.length > 0 ? (
              pendingInvitations.map((invitation) => (
                <InvitationListItem
                  key={invitation.id}
                  invitation={invitation}
                  canManage={isOwner}
                  onCancel={onCancelInvitation}
                  onResend={onResendInvitation}
                  activeAction={
                    activeInvitationAction && activeInvitationAction.id === invitation.id
                      ? activeInvitationAction.type
                      : null
                  }
                />
              ))
            ) : (
              <p className="text-sm text-muted-foreground">No pending invitations.</p>
            )}
          </div>
        </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

